import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import {
  Copy,
  ExternalLink,
  Link2,
  MoreHorizontal,
  Search,
  Trash2,
} from 'lucide-react';
import { useAtom } from 'jotai';
import { linksByFamilyAtom } from '../store';
import {
  useAuth,
  useFamily,
  useHeaderActions,
  useOnKeysArrived,
  useLoadingState,
} from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { api, ApiError, type FamilyLink } from '../lib/api';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import { ConfirmSheet } from '../components/ConfirmSheet';
import {
  fetchAllPages,
  isE2EEReady,
  matchesQuery,
  sameLink,
  saveSealedLink,
} from '../lib/e2ee/apps';
import { EmptyState } from '../components/EmptyState';
import { appLocale } from '../lib/format';
import { previewImageSrc } from '../lib/previewImage';
import { useLanguage, t as translateNow, useT } from '../i18n';
import { isUnbound, UnboundMark } from '../components/UnboundMark';

type Translate = ReturnType<typeof useT>;

function host(value: string): string {
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return value;
  }
}

// "Today", "Tue", "4 Oct".
function when(iso: string, t: Translate, now = new Date()): string {
  const date = new Date(iso);
  if (date.toDateString() === now.toDateString()) return t('apps.shared.today');
  const days = (now.getTime() - date.getTime()) / 86_400_000;
  if (days < 7)
    return date.toLocaleDateString(appLocale(), { weekday: 'short' });
  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

const WEEK_MS = 7 * 86_400_000;

// Links (Living Room): paste one to share, then the family's links by week,
// the newest as a big preview card.
export function LinksPage() {
  const { currentFamilyId, currentRole, families } = useFamily();
  const { currentUser } = useAuth();
  const toast = useToast();
  const t = useT();
  const language = useLanguage();
  const [linksMap, setLinksMap] = useAtom(linksByFamilyAtom);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // True from the start when there is a family to ask: the empty state
  // must never flash before the loader's empty area (M66, D248).
  const [loading, setLoading] = useState(Boolean(currentFamilyId));
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<FamilyLink | null>(null);

  const familyName =
    families.find((f) => f.id === currentFamilyId)?.name ??
    t('apps.shared.theFamily');
  const allLinks = currentFamilyId ? (linksMap[currentFamilyId] ?? []) : [];
  // Encrypted links (M28, D65) are searched here, not on the server.
  const sealedMode = isE2EEReady();
  const links =
    sealedMode && query.trim()
      ? allLinks.filter((link) =>
          matchesQuery(query, [link.url, link.title, link.note, link.siteName])
        )
      : allLinks;
  const canModerate = currentRole === 'root' || currentRole === 'admin';

  const setLinks = useCallback(
    (familyId: string, updater: (current: FamilyLink[]) => FamilyLink[]) => {
      setLinksMap((current) => ({
        ...current,
        [familyId]: updater(current[familyId] ?? []),
      }));
    },
    [setLinksMap]
  );

  const load = useCallback(
    async (familyId: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = isE2EEReady()
          ? await fetchAllPages((cursor) =>
              api.getFamilyLinks(familyId, { cursor })
            )
          : (
              await api.getFamilyLinks(familyId, {
                q: query.trim() || undefined,
              })
            ).data;
        setLinksMap((current) => ({ ...current, [familyId]: data }));
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : translateNow('apps.links.loadFailed')
        );
      } finally {
        setLoading(false);
      }
    },
    [query, setLinksMap]
  );

  // With encryption everything is loaded once and searched here.
  const serverQuery = sealedMode ? '' : query;
  // The mark while the links load (M66, D248).
  const linksState = useLoadingState({
    loading,
    hasData: links.length > 0,
    error,
    retry: () => {
      if (currentFamilyId) void load(currentFamilyId);
    },
  });
  useEffect(() => {
    if (!currentFamilyId) return;
    const timer = setTimeout(
      () => void load(currentFamilyId),
      serverQuery ? 300 : 0
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFamilyId, serverQuery]);
  useOnKeysArrived('family', currentFamilyId, () => {
    if (currentFamilyId) void load(currentFamilyId);
  });

  useHeaderActions(
    <IconButton
      aria-label={
        searchOpen ? t('apps.shared.closeSearch') : t('apps.links.search')
      }
      aria-pressed={searchOpen}
      icon={<Search size={22} />}
      variant="ghost"
      colorScheme="gray"
      size="lg"
      onClick={() => {
        setSearchOpen((open) => !open);
        if (searchOpen) setQuery('');
      }}
    />,
    [searchOpen, language]
  );

  const handleAdd = async () => {
    if (!currentFamilyId || !url.trim()) return;
    setSaving(true);
    try {
      if (sealedMode && allLinks.some((link) => sameLink(link.url, url))) {
        toast({ title: t('apps.links.alreadySaved'), status: 'info' });
        setUrl('');
        return;
      }
      const response = sealedMode
        ? await saveSealedLink(currentFamilyId, { url: url.trim() })
        : await api.addFamilyLink(currentFamilyId, { url: url.trim() });
      setLinks(currentFamilyId, (current) => [
        response.data,
        ...current.filter((link) => link.id !== response.data.id),
      ]);
      toast({
        title: response.data.alreadySaved
          ? t('apps.links.alreadySaved')
          : t('apps.links.shared'),
        status: response.data.alreadySaved ? 'info' : 'success',
      });
      setUrl('');
    } catch (err) {
      toast({
        title: t('apps.links.saveFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const confirmRemove = async () => {
    const link = removing;
    if (!currentFamilyId || !link) return;
    try {
      await api.deleteFamilyLink(currentFamilyId, link.id);
      setLinks(currentFamilyId, (current) =>
        current.filter((item) => item.id !== link.id)
      );
      toast({ title: t('apps.links.removed'), status: 'success' });
    } catch (err) {
      toast({
        title: t('apps.links.removeFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setRemoving(null);
    }
  };

  const copy = async (link: FamilyLink) => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast({
        title: t('apps.links.copied'),
        status: 'success',
        duration: 1500,
      });
    } catch {
      toast({ title: t('apps.links.copyFailed'), status: 'error' });
    }
  };

  if (!currentFamilyId) return <NoFamilySelected />;

  const now = Date.now();
  const thisWeek = links.filter(
    (link) => now - new Date(link.createdAt).getTime() < WEEK_MS
  );
  const earlier = links.filter(
    (link) => now - new Date(link.createdAt).getTime() >= WEEK_MS
  );
  const who = (link: FamilyLink) =>
    link.addedBy === currentUser?.id
      ? t('common.you')
      : (link.addedByUser?.displayName.split(' ')[0] ??
        t('apps.shared.family'));

  const menu = (link: FamilyLink) => (
    <Menu placement="bottom-end" isLazy>
      <MenuButton
        as={IconButton}
        aria-label={t('apps.shared.moreFor', {
          name: link.title ?? host(link.url),
        })}
        icon={<MoreHorizontal size={18} />}
        size="sm"
        variant="ghost"
        colorScheme="gray"
      />
      <MenuList>
        <MenuItem
          as="a"
          href={link.url}
          target="_blank"
          rel="noreferrer"
          icon={<ExternalLink size={16} />}
        >
          {t('apps.links.open')}
        </MenuItem>
        <MenuItem icon={<Copy size={16} />} onClick={() => void copy(link)}>
          {t('apps.links.copy')}
        </MenuItem>
        {(link.addedBy === currentUser?.id || canModerate) && (
          <MenuItem
            icon={<Trash2 size={16} />}
            color="danger"
            onClick={() => setRemoving(link)}
          >
            {t('apps.links.removeMenu')}
          </MenuItem>
        )}
      </MenuList>
    </Menu>
  );

  // Only a link's sealed picture; the site's address is never loaded (D320).
  const thumb = (link: FamilyLink, size: string) =>
    previewImageSrc(link) ? (
      <Image
        src={previewImageSrc(link)}
        alt=""
        w={size}
        h={size}
        objectFit="cover"
        borderRadius="14px"
        flexShrink={0}
        loading="lazy"
      />
    ) : (
      <Box
        w={size}
        h={size}
        borderRadius="14px"
        bg="gray.200"
        color="muted"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        aria-hidden="true"
      >
        <Link2 size={24} />
      </Box>
    );

  const meta = (link: FamilyLink) => (
    <HStack spacing="6px" fontSize="13px" color="muted" pt="2px">
      <Avatar
        name={link.addedByUser?.displayName}
        src={link.addedByUser?.avatarUrl ?? undefined}
        ownerId={link.addedByUser?.id}
        familyId={currentFamilyId}
        size="2xs"
      />
      <Text as="span" noOfLines={1}>
        {who(link)} · {when(link.createdAt, t)}
      </Text>
    </HStack>
  );

  const card = (link: FamilyLink, big: boolean) => (
    <Box
      key={link.id}
      bg="surface"
      borderWidth="1px"
      borderColor="line"
      borderRadius="card"
      overflow="hidden"
      position="relative"
    >
      <Box
        as="a"
        href={link.url}
        target="_blank"
        rel="noreferrer"
        display="block"
      >
        {big ? (
          <>
            {previewImageSrc(link) ? (
              <Image
                src={previewImageSrc(link)}
                alt=""
                h="140px"
                w="full"
                objectFit="cover"
                loading="lazy"
              />
            ) : (
              <Box
                h="120px"
                bg="gray.200"
                color="muted"
                display="flex"
                alignItems="center"
                justifyContent="center"
                aria-hidden="true"
              >
                <Link2 size={32} />
              </Box>
            )}
            <VStack
              align="stretch"
              spacing={1}
              px="14px"
              pt={3}
              pb="14px"
              pr={12}
            >
              <Text fontSize="13px" color="muted">
                {link.siteName ?? host(link.url)}
              </Text>
              <Text
                fontWeight={700}
                fontSize="17px"
                lineHeight={1.3}
                noOfLines={2}
              >
                {link.title ?? link.url}
              </Text>
              {isUnbound(link) && <UnboundMark record />}
              {link.note && (
                <Text fontSize="15px" color="ink.body" noOfLines={2}>
                  {link.note}
                </Text>
              )}
              {meta(link)}
            </VStack>
          </>
        ) : (
          <HStack spacing={3} p={3} pr={12} align="flex-start">
            {thumb(link, '76px')}
            <VStack align="stretch" spacing="2px" minW={0}>
              <Text fontSize="13px" color="muted" noOfLines={1}>
                {link.siteName ?? host(link.url)}
              </Text>
              <Text
                fontWeight={700}
                fontSize="16px"
                lineHeight={1.3}
                noOfLines={2}
              >
                {link.title ?? link.url}
              </Text>
              {isUnbound(link) && <UnboundMark record />}
              {meta(link)}
            </VStack>
          </HStack>
        )}
      </Box>
      <Box position="absolute" right={1} bottom={1}>
        {menu(link)}
      </Box>
    </Box>
  );

  const sectionLabel = (label: string) => (
    <Text fontSize="13px" fontWeight={700} color="muted" pt={1}>
      {label}
    </Text>
  );

  return (
    <Box h="full" overflowY="auto" sx={{ overscrollBehaviorY: 'contain' }}>
      <Box maxW="720px" mx="auto" px={4} pb={6}>
        {/* Paste a link to share */}
        <Box
          as="form"
          onSubmit={(event: React.FormEvent) => {
            event.preventDefault();
            void handleAdd();
          }}
          mb="14px"
        >
          <HStack
            spacing="6px"
            p="6px"
            pl={4}
            bg="surface"
            borderWidth="1px"
            borderColor="line"
            borderRadius="26px"
            _focusWithin={{ borderColor: 'accent' }}
          >
            <Box color="muted" aria-hidden="true">
              <Link2 size={20} />
            </Box>
            <Input
              type="url"
              inputMode="url"
              placeholder={t('apps.links.paste')}
              aria-label={t('apps.links.paste')}
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              variant="unstyled"
              h="40px"
              fontSize="17px"
            />
            <Button
              type="submit"
              h="40px"
              px={4}
              fontSize="15px"
              isLoading={saving}
              isDisabled={!url.trim()}
            >
              {t('apps.links.add')}
            </Button>
          </HStack>
        </Box>

        {searchOpen && (
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none" color="muted">
              <Search size={18} />
            </InputLeftElement>
            <Input
              autoFocus
              placeholder={t('apps.links.search')}
              aria-label={t('apps.links.search')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              borderRadius="full"
            />
          </InputGroup>
        )}

        {error && !linksState.failed && (
          <Alert status="error" borderRadius="xl" mb={3}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {linksState.show ? (
          <LoadingPanel state={linksState} label={t('apps.links.loading')} />
        ) : links.length === 0 ? (
          <EmptyState
            kind="links"
            title={
              query ? t('apps.shared.nothingHere') : t('apps.links.emptyTitle')
            }
            body={
              query
                ? t('apps.links.noMatch')
                : t('apps.links.emptyBody', { family: familyName })
            }
          />
        ) : (
          <VStack align="stretch" spacing="10px">
            {thisWeek.length > 0 && sectionLabel(t('apps.links.thisWeek'))}
            {thisWeek.map((link, i) => card(link, i === 0))}
            {earlier.length > 0 && sectionLabel(t('apps.links.earlier'))}
            {earlier.map((link) =>
              card(link, thisWeek.length === 0 && link === earlier[0])
            )}
          </VStack>
        )}
      </Box>

      <ConfirmSheet
        isOpen={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('apps.links.removeTitle')}
        body={t('apps.links.removeBody', { family: familyName })}
        preview={
          removing && (
            <HStack spacing={3} p="10px" borderRadius="18px" bg="canvas">
              {thumb(removing, '52px')}
              <Box minW={0}>
                <Text fontWeight={700} fontSize="16px" noOfLines={1}>
                  {removing.title ?? host(removing.url)}
                </Text>
                <Text fontSize="14px" color="muted">
                  {t('apps.links.addedBy', { name: who(removing) })} ·{' '}
                  {removing.siteName ?? host(removing.url)}
                </Text>
              </Box>
            </HStack>
          )
        }
        confirmLabel={t('apps.links.removeConfirm')}
        confirmIcon={<Trash2 size={18} />}
        cancelLabel={t('apps.shared.keepIt')}
        onConfirm={() => void confirmRemove()}
      />
    </Box>
  );
}
